import React, { useEffect, useState } from "react";
import { api, getUser } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Award, Send, ChevronDown, ChevronUp } from "lucide-react";

/** Admin card: nudge learners who finished everything but haven't claimed their certificate. */
export const CertificateReminderCard = () => {
  const [rows, setRows] = useState(null); // null = loading
  const [expanded, setExpanded] = useState(false);
  const [sending, setSending] = useState(null); // "test" | "all" | null
  const [confirmStep, setConfirmStep] = useState(false);

  const load = () => {
    api.get("/program-certificate/reminders")
      .then(({ data }) => setRows(data.rows || []))
      .catch(() => setRows([]));
  };
  useEffect(() => { load(); }, []);

  const send = async (testOnly) => {
    setSending(testOnly ? "test" : "all");
    try {
      const payload = testOnly ? { test_email: getUser()?.email } : {};
      const { data } = await api.post("/program-certificate/reminders/send", payload);
      if (testOnly) toast.success(`Test reminder sent to ${getUser()?.email}`);
      else toast.success(`Reminder sent to ${data.sent} of ${data.learner_count} learners`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send reminders.");
    } finally {
      setSending(null);
    }
  };

  if (rows === null) return null;

  return (
    <Card className="p-5 border-amber-300/60 bg-gradient-to-r from-amber-50 to-white mb-8" data-testid="cert-reminder-card">
      <div className="flex items-center gap-4">
        <div className="shrink-0 w-11 h-11 rounded-lg bg-amber-100 flex items-center justify-center">
          <Award size={20} className="text-amber-700" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-slate-900">Certificate reminders</div>
          <div className="text-sm text-slate-600" data-testid="cert-reminder-count">
            {rows.length === 0
              ? "Everyone who's eligible has already claimed their certificate. Nothing to send."
              : rows.length === 1
                ? <>1 learner has finished everything but hasn&rsquo;t claimed their certificate yet.</>
                : <>{rows.length} learners have finished everything but haven&rsquo;t claimed their certificate yet.</>}
          </div>
        </div>
        {rows.length > 0 && (
          <>
            <button
              type="button"
              data-testid="cert-reminder-toggle-list"
              onClick={() => setExpanded((v) => !v)}
              className="text-sm text-amber-800 hover:text-amber-900 inline-flex items-center gap-1 shrink-0"
            >
              {expanded ? <>Hide list <ChevronUp size={14} /></> : <>See who <ChevronDown size={14} /></>}
            </button>
            <Button
              variant="outline"
              data-testid="cert-reminder-test"
              disabled={!!sending}
              onClick={() => send(true)}
              className="shrink-0"
            >
              {sending === "test" ? "Sending…" : "Send me a test"}
            </Button>
            <Button
              data-testid="cert-reminder-send"
              disabled={!!sending}
              onClick={() => {
                if (!confirmStep) {
                  setConfirmStep(true);
                  setTimeout(() => setConfirmStep(false), 6000);
                  return;
                }
                setConfirmStep(false);
                send(false);
              }}
              className={`shrink-0 text-white ${confirmStep ? "bg-[#8f1f1d] hover:bg-[#7a1a18]" : "bg-amber-600 hover:bg-amber-700"}`}
            >
              <Send size={14} className="mr-1.5" />
              {sending === "all" ? "Sending…" : confirmStep ? `Really email ${rows.length}?` : `Remind ${rows.length} learner${rows.length === 1 ? "" : "s"}`}
            </Button>
          </>
        )}
      </div>
      {expanded && rows.length > 0 && (
        <div className="mt-4 pt-4 border-t border-amber-200/60 grid sm:grid-cols-2 gap-x-6 gap-y-1.5" data-testid="cert-reminder-list">
          {rows.map((r) => (
            <div key={r.email} className="flex items-center gap-2 text-sm" data-testid={`cert-reminder-row-${r.learner_id || r.email}`}>
              <span className="font-medium text-slate-800 truncate">{r.name}</span>
              <span className="text-xs text-slate-400 truncate">{r.email}</span>
              <span className="ml-auto text-[11px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 whitespace-nowrap">
                {r.granted ? "granted" : `${r.passed_quizzes}/${r.total_quizzes} passed`}
              </span>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
};

export default CertificateReminderCard;
